import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Paper,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  GraphicEq,
  Visibility,
  Search,
  ErrorOutline,
  TrendingUp,
  Schedule,
  Bolt,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../landingPage/Sidebar';
import { PrimaryButton } from '../../components/ui/button';
import { PrimaryTextField } from '../../components/ui/input';
import { DataTable } from '../../components/ui/table';
import { PrimaryBreadcrumb } from '../../components/ui/breadcrumb';
import SuccessIcon from './images/SuccessIcon.png';
import NlpIcon from './images/nlpIcon.png';
import Icon from './images/Icon.png';

const NaturalLanguageSearch = () => {
  const navigate = useNavigate();
  const [nlpQuery, setNlpQuery] = useState('');
  const [searchExecuted, setSearchExecuted] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [projectName, setProjectName] = useState('Project HTL');
  const [isEditingProjectName, setIsEditingProjectName] = useState(false);

  // Initialize with dummy data matching the image
  React.useEffect(() => {
    if (searchExecuted) {
      setSearchResults([
        {
          srNo: 1,
          timestamp: '2026-01-29T14:32:18.245Z',
          level: 'ERROR',
          message: 'Connection pool exhausted: timeout acquiring database connection after 30000ms',
          source: 'payment-gateway-api',
          host: 'prod-useast-web-01',
        },
        {
          srNo: 2,
          timestamp: '2026-01-29T14:32:18.245Z',
          level: 'ERROR',
          message: 'Transaction rollback initiated: insufficient account balance for transfer operation',
          source: 'transaction-processor',
          host: 'prod-useast-app-03',
        },
        {
          srNo: 3,
          timestamp: '2026-01-29T14:32:18.245Z',
          level: 'ERROR',
          message: 'JWT token validation failed: signature verification error with auth provider',
          source: 'authentication-service',
          host: 'prod-useast-storage-01',
        },
        {
          srNo: 4,
          timestamp: '2026-01-29T14:32:18.245Z',
          level: 'ERROR',
          message: 'Redis cache unavailable: connection refused on port 6379, falling back to database',
          source: 'prod-useast-cache-01',
          host: 'prod-useast-storage-01',
        },
        {
          srNo: 5,
          timestamp: '2026-01-29T14:32:18.245Z',
          level: 'ERROR',
          message: 'Transaction rollback initiated: insufficient account balance for transfer operation',
          source: 'prod-useast-cache-01',
          host: 'prod-useast-storage-01',
        },
        {
          srNo: 6,
          timestamp: '2026-01-29T14:32:18.245Z',
          level: 'ERROR',
          message: 'Connection pool exhausted: timeout acquiring database connection after 30000ms',
          source: 'prod-useast-cache-01',
          host: 'prod-useast-storage-01',
        },
        {
          srNo: 7,
          timestamp: '2026-01-29T14:32:18.245Z',
          level: 'ERROR',
          message: 'JWT token validation failed: signature verification error with auth provider',
          source: 'authentication-service',
          host: 'prod-useast-storage-01',
        },
        {
          srNo: 8,
          timestamp: '2026-01-29T14:32:18.245Z',
          level: 'ERROR',
          message: 'Transaction rollback initiated: insufficient account balance for transfer operation',
          source: 'transaction-processor',
          host: 'prod-useast-app-03',
        },
      ]);
      setIsRunning(true);
    }
  }, [searchExecuted]);

  const handleRunSearch = () => {
    if (nlpQuery.trim()) {
      setSearchExecuted(true);
    }
  };

  const handleCancelSearch = () => {
    setNlpQuery('');
    setSearchExecuted(false);
    setSearchResults([]);
    setIsRunning(false);
  };

  const handlePopularSearch = (query) => {
    setNlpQuery(query);
  };

  const handleSave = () => {
    console.log('Save clicked');
  };

  const handlePause = () => {
    setIsRunning(false);
    console.log('Pause clicked');
  };

  const handleStop = () => {
    setIsRunning(false);
    setSearchExecuted(false);
    setSearchResults([]);
    console.log('Stop clicked');
  };

  const handleViewLiveStreaming = () => {
    navigate('/nlp-live-streaming', { state: { viewMode: 'streaming' } });
  };

  const handleDataPreview = () => {
    navigate('/nlp-live-streaming', { state: { viewMode: 'preview' } });
  };

  const popularSearches = [
    { query: 'Errors from local logs', icon: ErrorOutline },
    { query: 'High latency requests', icon: TrendingUp },
    { query: 'Warnings in last 24 hours', icon: Schedule },
    { query: 'Top error messages by count', icon: Bolt },
  ];

  const queryInterpretation = {
    dataSource: 'production.logs.system',
    eventType: 'application/error',
    queryFilter: 'severity_level = ERROR',
    timeWindow: '2026-01-28 14:00 - 2026-01-29 14:00 UTC',
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        bgcolor: '#FAFBFD',
        minHeight: '100vh',
        overflow: 'hidden',
      }}
    >
      <Sidebar />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* Top Header Bar - Dark Blue */}
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            py: { xs: 1, sm: 1.5 },
            bgcolor: '#0F172A',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => window.history.back()}
              sx={{
                p: 0.5,
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <ArrowBack sx={{ fontSize: 20 }} />
            </IconButton>
            {isEditingProjectName ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PrimaryTextField
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  onBlur={() => setIsEditingProjectName(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setIsEditingProjectName(false);
                    }
                  }}
                  autoFocus
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: 'white' },
                    },
                    input: { color: 'white', fontSize: '0.875rem', py: 0.5 },
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: 'white',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  {projectName}...
                </Typography>
                <IconButton
                  onClick={() => setIsEditingProjectName(true)}
                  sx={{
                    p: 0.25,
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                  }}
                >
                  <Edit sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Action Buttons - Only show when search is executed */}
            {searchExecuted && (
              <>
                 <PrimaryButton
                   variant="outlined"
                   onClick={handleSave}
                   sx={{
                     fontSize: '0.8125rem',
                     textTransform: 'none',
                     px: 2,
                     color: 'white',
                     borderColor: 'rgba(255, 255, 255, 0.3)',
                     '&:hover': {
                       borderColor: 'rgba(255, 255, 255, 0.5)',
                       bgcolor: 'rgba(255, 255, 255, 0.1)',
                     },
                   }}
                 >
                   Save
                 </PrimaryButton>
                 <PrimaryButton
                   variant="outlined"
                   onClick={handlePause}
                   sx={{
                     fontSize: '0.8125rem',
                     textTransform: 'none',
                     px: 2,
                     color: 'white',
                     borderColor: 'rgba(255, 255, 255, 0.3)',
                     '&:hover': {
                       borderColor: 'rgba(255, 255, 255, 0.5)',
                       bgcolor: 'rgba(255, 255, 255, 0.1)',
                     },
                   }}
                 >
                   Pause
                 </PrimaryButton>
                 <PrimaryButton
                   variant="outlined"
                   onClick={handleStop}
                   sx={{
                     fontSize: '0.8125rem',
                     textTransform: 'none',
                     px: 2,
                     color: '#FF4444',
                     borderColor: '#FF4444',
                     bgcolor: 'transparent',
                     '&:hover': {
                       borderColor: '#FF6666',
                       bgcolor: 'rgba(255, 68, 68, 0.1)',
                     },
                   }}
                 >
                   Stop
                 </PrimaryButton>
                {isRunning && (
                  <Chip
                    icon={<GraphicEq sx={{ fontSize: 16, color: '#008236' }} />}
                    label="Running"
                    sx={{
                      height: 28,
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      fontFamily: 'Inter, sans-serif',
                      bgcolor: '#DCFCE7',
                      color: '#008236',
                      border: '1px solid #008236',
                      borderRadius: '4px',
                      '& .MuiChip-icon': {
                        marginLeft: '8px',
                        marginRight: '0',
                      },
                    }}
                  />
                )}
              </>
            )}
          </Box>
        </Box>

        {/* Breadcrumbs */}
        {searchExecuted && (
          <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: 1, bgcolor: '#FAFBFD', borderBottom: '1px solid #E5E7EB', flexShrink: 0 }}>
            <PrimaryBreadcrumb
              items={[
                { label: 'Workflow', path: '/workflow' },
                { label: 'NLP Builder' },
              ]}
              sx={{ mb: 0 }}
            />
          </Box>
        )}

        {/* Main Content Area - Two Column Layout when search executed */}
        {searchExecuted ? (
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, bgcolor: '#FAFBFD', minWidth: 0, overflow: 'hidden' }}>
            {/* Left Sidebar */}
            <Box
              sx={{
                width: { xs: '100%', md: '320px' },
                minWidth: { xs: '100%', md: '320px' },
                maxWidth: { xs: '100%', md: '320px' },
                bgcolor: '#F9FAFB',
                borderRight: { xs: 'none', md: '1px solid #E5E7EB' },
                borderBottom: { xs: '1px solid #E5E7EB', md: 'none' },
                p: { xs: 1.5, sm: 1.5 },
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                flexShrink: 0,
              }}
            >
              {/* Natural Language Input Section */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <Box component="img" src={NlpIcon} alt="NLP Icon" sx={{ width: 16, height: 16 }} />
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#0F172A',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    Natural Language Input
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.8125rem',
                    color: '#6B7280',
                    fontFamily: 'Inter, sans-serif',
                    mb: 1.5,
                  }}
                >
                  Describe your streaming logic in plain English
                </Typography>
                <Box sx={{ position: 'relative', mb: 1.5 }}>
                  <PrimaryTextField
                    multiline
                    rows={5}
                    fullWidth
                    placeholder="Describe your streaming logic in plain English"
                    value={nlpQuery}
                    onChange={(e) => setNlpQuery(e.target.value)}
                    inputProps={{ maxLength: 500 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, sans-serif',
                        borderRadius: '4px',
                        bgcolor: 'white',
                      },
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 12,
                      fontSize: '0.75rem',
                      color: '#6B7280',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    {nlpQuery.length}/500
                  </Box>
                </Box>

                {/* Validated Status Box */}
                <Box
                  sx={{
                    p: 1.25,
                    bgcolor: '#DCFCE7',
                    border: '1px solid #008236',
                    borderRadius: '6px',
                    mb: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      color: '#008236',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    Validated
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#008236',
                      fontFamily: 'Inter, sans-serif',
                      mt: 0.5,
                    }}
                  >
                    Query interpretation complete
                  </Typography>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <PrimaryButton
                    variant="contained"
                    onClick={handleViewLiveStreaming}
                    startIcon={<GraphicEq sx={{ fontSize: 16 }} />}
                    sx={{
                      fontSize: '0.8125rem',
                      textTransform: 'none',
                      bgcolor: '#008236',
                      '&:hover': { bgcolor: '#006B2E' },
                    }}
                  >
                    View Live Streaming
                  </PrimaryButton>
                  <PrimaryButton
                    variant="outlined"
                    onClick={handleDataPreview}
                    startIcon={<Visibility sx={{ fontSize: 16 }} />}
                    sx={{
                      fontSize: '0.8125rem',
                      textTransform: 'none',
                      bgcolor: 'white',
                      borderColor: '#E5E7EB',
                      color: '#525252',
                      '&:hover': {
                        bgcolor: '#F9FAFB',
                        borderColor: '#D1D5DB',
                      },
                    }}
                  >
                    Data Preview
                  </PrimaryButton>
                </Box>
              </Box>
            </Box>

            {/* Right Main Content Area */}
            <Box sx={{ flexGrow: 1, minWidth: 0, px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2 }, bgcolor: 'white', overflow: 'auto', minHeight: 0 }}>
              {/* Query Interpretation - Compact */}
              <Card
                sx={{
                  mb: 2,
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 1.75 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography
                      sx={{
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        color: '#0F172A',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      Query Interpretation
                    </Typography>
                    <Chip
                      icon={
                        <Box
                          component="img"
                          src={SuccessIcon}
                          alt="Success"
                          sx={{ width: 12, height: 12 }}
                        />
                      }
                      label="Validated"
                      sx={{
                        height: 22,
                        fontSize: '0.6875rem',
                        fontWeight: 500,
                        fontFamily: 'Inter, sans-serif',
                        bgcolor: '#DCFCE7',
                        color: '#008236',
                        border: '1px solid #008236',
                        borderRadius: '4px',
                        '& .MuiChip-icon': {
                          marginLeft: '4px',
                          marginRight: '0',
                        },
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6B7280',
                      fontFamily: 'Inter, sans-serif',
                      mb: 1,
                    }}
                  >
                    Your natural language request has been parsed and mapped to the following execution parameters
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper
                        sx={{
                          p: 0.75,
                          bgcolor: '#F1F5FF',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.625rem',
                            color: '#000000',
                            fontFamily: 'Inter, sans-serif',
                            mb: 0.25,
                          }}
                        >
                          Data Source
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.6875rem',
                            fontWeight: 500,
                            color: '#4A5565',
                            fontFamily: 'Inter, sans-serif',
                          }}
                        >
                          {queryInterpretation.dataSource}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper
                        sx={{
                          p: 0.75,
                          bgcolor: '#F1F5FF',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.625rem',
                            color: '#000000',
                            fontFamily: 'Inter, sans-serif',
                            mb: 0.25,
                          }}
                        >
                          Event Type
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.6875rem',
                            fontWeight: 500,
                            color: '#4A5565',
                            fontFamily: 'Inter, sans-serif',
                          }}
                        >
                          {queryInterpretation.eventType}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper
                        sx={{
                          p: 0.75,
                          bgcolor: '#F1F5FF',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.625rem',
                            color: '#000000',
                            fontFamily: 'Inter, sans-serif',
                            mb: 0.25,
                          }}
                        >
                          Query Filter
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.6875rem',
                            fontWeight: 500,
                            color: '#4A5565',
                            fontFamily: 'Inter, sans-serif',
                          }}
                        >
                          {queryInterpretation.queryFilter}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper
                        sx={{
                          p: 0.75,
                          bgcolor: '#F1F5FF',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.625rem',
                            color: '#000000',
                            fontFamily: 'Inter, sans-serif',
                            mb: 0.25,
                          }}
                        >
                          Time Window
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.6875rem',
                            fontWeight: 500,
                            color: '#4A5565',
                            fontFamily: 'Inter, sans-serif',
                          }}
                        >
                          {queryInterpretation.timeWindow}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Results Section - Highlighted */}
              <Card
                sx={{
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                  bgcolor: 'white',
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#0F172A',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      Results
                    </Typography>
                    <Chip
                      icon={
                        <Box
                          component="img"
                          src={SuccessIcon}
                          alt="Success"
                          sx={{ width: 14, height: 14 }}
                        />
                      }
                      label="Search Complete"
                      sx={{
                        height: 24,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        fontFamily: 'Inter, sans-serif',
                        bgcolor: '#DCFCE7',
                        color: '#008236',
                        border: '1px solid #008236',
                        borderRadius: '4px',
                        '& .MuiChip-icon': {
                          marginLeft: '6px',
                          marginRight: '0',
                        },
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.8125rem',
                      color: '#6B7280',
                      fontFamily: 'Inter, sans-serif',
                      mb: 1.5,
                    }}
                  >
                    Found 247 results • Showing 1-8
                  </Typography>
                  <DataTable
                    columns={[
                      {
                        key: 'srNo',
                        label: 'Sr.No',
                        sortable: true,
                        cellSx: {
                          fontSize: '0.8125rem',
                          color: '#525252',
                          fontFamily: 'Inter, sans-serif',
                        },
                      },
                      {
                        key: 'timestamp',
                        label: 'Timestamp',
                        sortable: true,
                        cellSx: {
                          fontSize: '0.8125rem',
                          color: '#525252',
                          fontFamily: 'Inter, sans-serif',
                        },
                      },
                      {
                        key: 'level',
                        label: 'Level',
                        sortable: true,
                        render: (row) => (
                          <Chip
                            label={row.level}
                            sx={{
                              height: 24,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              fontFamily: 'Inter, sans-serif',
                              bgcolor: '#FFE2E2',
                              color: '#C10007',
                              borderRadius: '12px',
                            }}
                          />
                        ),
                      },
                      {
                        key: 'message',
                        label: 'Message',
                        sortable: true,
                        cellSx: {
                          fontSize: '0.8125rem',
                          color: '#525252',
                          fontFamily: 'Inter, sans-serif',
                        },
                      },
                      {
                        key: 'source',
                        label: 'Source',
                        sortable: true,
                        cellSx: {
                          fontSize: '0.8125rem',
                          color: '#525252',
                          fontFamily: 'Inter, sans-serif',
                        },
                      },
                      {
                        key: 'host',
                        label: 'Host',
                        sortable: true,
                        cellSx: {
                          fontSize: '0.8125rem',
                          color: '#525252',
                          fontFamily: 'Inter, sans-serif',
                        },
                      },
                    ]}
                    data={searchResults}
                    loading={false}
                    showSerialNumber={false}
                    emptyMessage="No results found."
                  />
                </CardContent>
              </Card>
            </Box>
          </Box>
        ) : (
          /* Single Column Layout - Before Search */
          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              px: { xs: 1.5, sm: 2, md: 3 },
              py: { xs: 1.5, sm: 2 },
              bgcolor: '#FAFBFD',
              overflow: 'auto',
            }}
          >
          {/* Query Your Data Section */}
          <Card
            sx={{
              mb: 3,
              borderRadius: '4px',
              border: '1px solid #E5E7EB',
              boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
            }}
          >
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #3B9562 0%,rgb(56, 123, 85) 50%, #16a34a 100%)',
                      borderRadius: '6px',
                      p: 1,
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box component="img" src={Icon} alt="NLP Icon" sx={{ width: 16, height: 16 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 650,
                      color: '#0F172A',
                      fontFamily: 'Inter, sans-serif',
                      lineHeight: '1.5',
                    }}
                  >
                    Query Your Data
                  </Typography>
                </Box>
                {/* Action Buttons - Always visible */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <PrimaryButton
                    variant="contained"
                    onClick={handleViewLiveStreaming}
                    startIcon={<GraphicEq sx={{ fontSize: 16 }} />}
                    sx={{
                      fontSize: '0.8125rem',
                      textTransform: 'none',
                      px: 2,
                      py: 0.75,
                      bgcolor: '#008236',
                      borderRadius: '6px',
                      boxShadow: '0px 1px 2px rgba(0, 130, 54, 0.2)',
                      '&:hover': {
                        bgcolor: '#006B2E',
                        boxShadow: '0px 2px 4px rgba(0, 130, 54, 0.3)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    View Live Streaming
                  </PrimaryButton>
                  <PrimaryButton
                    variant="outlined"
                    onClick={handleDataPreview}
                    startIcon={<Visibility sx={{ fontSize: 16 }} />}
                    sx={{
                      fontSize: '0.8125rem',
                      textTransform: 'none',
                      px: 2,
                      py: 0.75,
                      bgcolor: 'white',
                      borderColor: '#E5E7EB',
                      color: '#525252',
                      borderRadius: '6px',
                      '&:hover': {
                        bgcolor: '#F9FAFB',
                        borderColor: '#008236',
                        color: '#008236',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Data Preview
                  </PrimaryButton>
                </Box>
              </Box>
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  color: '#6B7280',
                  fontFamily: 'Inter, sans-serif',
                  mb: 1.5,
                }}
              >
                Describe your data requirements using natural language
              </Typography>
              <Box sx={{ position: 'relative', mb: 1.5 }}>
                <PrimaryTextField
                  multiline
                  rows={4}
                  fullWidth
                  placeholder="Get error logs from local files."
                  value={nlpQuery}
                  onChange={(e) => setNlpQuery(e.target.value)}
                  inputProps={{ maxLength: 500 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, sans-serif',
                      borderRadius: '4px',
                    },
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 12,
                    fontSize: '0.75rem',
                    color: '#6B7280',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {nlpQuery.length}/500
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <PrimaryButton
                  variant="outlined"
                  onClick={handleCancelSearch}
                  sx={{
                    fontSize: '0.8125rem',
                    textTransform: 'none',
                    px: 2,
                    bgcolor: 'white',
                    borderColor: '#E5E7EB',
                    color: '#525252',
                    '&:hover': {
                      bgcolor: '#F9FAFB',
                      borderColor: '#D1D5DB',
                    },
                  }}
                >
                  Cancel
                </PrimaryButton>
                <PrimaryButton
                  variant="contained"
                  onClick={handleRunSearch}
                  disabled={!nlpQuery.trim()}
                  startIcon={<Search sx={{ fontSize: 16 }} />}
                  sx={{
                    fontSize: '0.8125rem',
                    textTransform: 'none',
                    px: 2,
                  }}
                >
                  Run Search
                </PrimaryButton>
              </Box>
            </CardContent>
          </Card>

            {/* Popular Searches */}
            <Card
              sx={{
                mb: 3,
                borderRadius: '4px',
                border: '1px solid #E5E7EB',
                boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography
                  sx={{
                    fontSize: '0.8125rem',
                    color: '#6B7280',
                    fontFamily: 'Inter, sans-serif',
                    mb: 1.5,
                  }}
                >
                  Try these popular searches:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {popularSearches.map((item, index) => {
                    const IconComponent = item.icon;
                    return (
                      <Box
                        key={index}
                        onClick={() => handlePopularSearch(item.query)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1.5,
                          py: 0.75,
                          borderRadius: '6px',
                          border: '1px solid #E5E7EB',
                          bgcolor: '#F1F5FF',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: '#F9FAFB',
                            borderColor: '#D1D5DB',
                          },
                        }}
                      >
                        <IconComponent sx={{ fontSize: 16, color: '#008236' }} />
                        <Typography
                          sx={{
                            fontSize: '0.8125rem',
                            color: '#3C9663',
                            fontFamily: 'Inter, sans-serif',
                          }}
                        >
                          {item.query}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NaturalLanguageSearch;
